import { Component, OnInit} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {
  CallbackPayload,
  ConfigService,
  LighthouseBrandListDisplayItem,
  LighthouseEndpointListDisplayItem,
  LighthousePortalListDisplayItem,
  SourceCredentialType,
  VaultProfileConfig,
} from '@shared-library';
import {NGXLogger} from 'ngx-logger';
import {CommonModule, KeyValue} from "@angular/common";
import {
  FdkButtonComponent,
  FdkCardComponent,
  FdkCheckboxComponent,
  FdkIconComponent,
  FdkIllustrationComponent,
} from 'fasten-design-kit';

const DEFAULT_SOURCE_LOGO_BRAND_ID = 'ee170dc1-d2ef-4dfa-a2fb-b30335244aa6';

type AccountWithName = {
  portal?: {name?: string};
  brand?: {name?: string};
};

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: true,
    imports: [
      CommonModule, // Not needed if you don't use any common directives
      RouterModule,
      FdkIllustrationComponent,
      FdkButtonComponent,
      FdkCardComponent,
      FdkCheckboxComponent,
      FdkIconComponent,
    ]
})
export class DashboardComponent implements OnInit {
  private readonly nameCollator = new Intl.Collator(undefined, {
    sensitivity: 'base',
    numeric: true,
  });

  readonly compareAccountEntriesByName = (
    a: KeyValue<string, AccountWithName>,
    b: KeyValue<string, AccountWithName>,
  ): number => this.compareAccountNames(a.value, b.value) || a.key.localeCompare(b.key);

  sortAccountsByName<T extends AccountWithName>(accounts?: readonly T[] | null): T[] {
    return [...(accounts || [])].sort((a, b) => this.compareAccountNames(a, b));
  }

  private compareAccountNames(a: AccountWithName, b: AccountWithName): number {
    return this.nameCollator.compare(
      a.portal?.name || a.brand?.name || '',
      b.portal?.name || b.brand?.name || '',
    );
  }

  constructor(
    public configService: ConfigService,
    private router: Router,
    private logger: NGXLogger,
  ) { }

  ngOnInit(): void {
    if (this.configService.systemConfig$.tefcaMode && !this.configService.vaultProfileConfig$.rlsQueryComplete) {
      void this.router.navigateByUrl('/dashboard/loading', {
        state: {skipIdentityVerification: true},
      });
      return;
    }
  }

  get emptyTefcaRLSResult(): boolean {
    const vaultProfile = this.configService.vaultProfileConfig$;
    const resultCount =
      Object.keys(vaultProfile.discoveredPatientAccounts || {}).length +
      Object.keys(vaultProfile.pendingPatientAccounts || {}).length +
      (vaultProfile.connectedPatientAccounts?.length || 0);

    return !!(
      (!this.configService.systemConfig$.tefcaMode || vaultProfile.rlsQueryComplete) &&
      resultCount === 0
    );
  }

  connectAccount(externalState:string, pendingAccount: { brand: LighthouseBrandListDisplayItem, portal: LighthousePortalListDisplayItem, endpoint: LighthouseEndpointListDisplayItem, vault_profile_connection_id?: string }){
    this.logger.info("connecting account", pendingAccount)
    this.router.navigate(['dashboard/connecting'], {
      queryParams: {
        "brandId": pendingAccount.brand?.id,
        "portalId": pendingAccount.portal?.id,
        "endpointId": pendingAccount.endpoint?.id,
        "externalId": this.configService.systemConfig$.externalId,
        "externalState": externalState,
        "vaultProfileConnectionId": pendingAccount.vault_profile_connection_id
      }
    })
  }

  // Add patientAuthorizationType and this will basically be in the backend SourceCredentialType
  connectTefcaDirectAccount(externalState:string, pendingAccount: { brand: LighthouseBrandListDisplayItem, portal: LighthousePortalListDisplayItem, endpoint: LighthouseEndpointListDisplayItem, vault_profile_connection_id?: string, patient_auth_type?: SourceCredentialType }){
    this.configService.vaultProfileAddConnectedAccount(
     {
        external_state: externalState,
        org_connection_id: "",
        connection_status: "connected",
        platform_type: "tefca",
        brand_id: pendingAccount.brand?.id,
        portal_id: pendingAccount.portal?.id,
        endpoint_id: pendingAccount.endpoint?.id,
        vault_profile_connection_id: pendingAccount.vault_profile_connection_id,
        patient_auth_type: pendingAccount.patient_auth_type || SourceCredentialType.SourceCredentialTypeTefcaDirect
     } as CallbackPayload
   )
  }

  deselectTefcaDirectAccount(vaultProfileConnectionId?: string): void {
    if (!vaultProfileConnectionId) {
      return
    }

    this.configService.vaultProfileDeselectTefcaDirectConnectedAccount(
      vaultProfileConnectionId,
    )
  }

  toggleAllAccounts(checked: boolean, vaultProfile: VaultProfileConfig): void {
    if (checked) {
      Object.entries(vaultProfile.discoveredPatientAccounts || {}).forEach(
        ([externalState, account]) => {
          this.connectTefcaDirectAccount(externalState, account)
        },
      )
      return
    }

    const connectedAccounts = [...(vaultProfile.connectedPatientAccounts || [])]
    connectedAccounts.forEach((account) => {
      this.deselectTefcaDirectAccount(account.vault_profile_connection_id)
    })
  }

  areAllAccountsSelected(vaultProfile: VaultProfileConfig): boolean {
    const discoveredCount = Object.keys(
      vaultProfile.discoveredPatientAccounts || {},
    ).length
    const connectedCount = (vaultProfile.connectedPatientAccounts || []).filter(
      (account) => !!account.vault_profile_connection_id,
    ).length

    return connectedCount > 0 && discoveredCount === 0
  }

  areSomeAccountsSelected(vaultProfile: VaultProfileConfig): boolean {
    const discoveredCount = Object.keys(
      vaultProfile.discoveredPatientAccounts || {},
    ).length
    const connectedCount = (vaultProfile.connectedPatientAccounts || []).filter(
      (account) => !!account.vault_profile_connection_id,
    ).length

    return connectedCount > 0 && discoveredCount > 0
  }

  hasSelectableAccounts(vaultProfile: VaultProfileConfig): boolean {
    return (
      Object.keys(vaultProfile.discoveredPatientAccounts || {}).length > 0 ||
      (vaultProfile.connectedPatientAccounts || []).some(
        (account) => !!account.vault_profile_connection_id,
      )
    )
  }

  getAccountInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean)

    if (words.length > 1) {
      return `${words[0][0]}${words[1][0]}`
    }

    return words[0]?.slice(0, 2) || ''
  }

  getAccountLogo(brandId?: string): string {
    if (!brandId || brandId === DEFAULT_SOURCE_LOGO_BRAND_ID) {
      return ''
    }

    return `https://cdn.fastenhealth.com/logos/sources/${brandId}.png`
  }

  confirmAccounts(): Promise<boolean> {
    return this.router.navigateByUrl('/dashboard/confirm');
  }


  //TODO1: add helper to process individual success, failure and catastrophic failures from aturhoizeTefcaDirect, and send messageBus events
  //TODO2: promote pending connections to connected list, so they will be sent to the customer via the Completed event.



  shouldShowMultiConnectHint(vaultProfile?: VaultProfileConfig | null): boolean {
    if (!vaultProfile) {
      return false
    }

    const pendingCount = Object.keys(vaultProfile.pendingPatientAccounts || {}).length
    const discoveredCount = Object.keys(vaultProfile.discoveredPatientAccounts || {}).length
    const connectedCount = vaultProfile.connectedPatientAccounts?.length ?? 0

    const firstPendingQueued = pendingCount === 1 && connectedCount === 0 && discoveredCount === 0
    const onlyOneConnected = connectedCount === 1 && pendingCount === 0 && discoveredCount === 0

    return firstPendingQueued || onlyOneConnected
  }
}
